import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type HealthStatus = 'red' | 'yellow' | 'green';

export interface HostHealthRow {
  producer_id: string;
  producer_name: string;
  producer_slug: string;
  status: HealthStatus;
  error_count: number;
  warn_count: number;
  latest_event_key: string;
  latest_level: string;
  latest_message: string;
  latest_at: string;
}

type TimeRange = '24h' | '7d' | '30d';

function rangeToDate(range: TimeRange): string {
  const now = new Date();
  switch (range) {
    case '24h': now.setHours(now.getHours() - 24); break;
    case '7d': now.setDate(now.getDate() - 7); break;
    case '30d': now.setDate(now.getDate() - 30); break;
  }
  return now.toISOString();
}

export function useHostHealth(range: TimeRange) {
  return useQuery({
    queryKey: ['host-health', range],
    staleTime: 60_000,
    queryFn: async (): Promise<HostHealthRow[]> => {
      const since = rangeToDate(range);

      const { data, error } = await supabase
        .from('system_events')
        .select(`
          id, event_key, level, message, created_at, producer_id,
          producer:producers!system_events_producer_id_fkey(id, name, slug)
        `)
        .in('level', ['error', 'warn'])
        .not('producer_id', 'is', null)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Group by producer_id
      const map = new Map<string, {
        producer_id: string;
        producer_name: string;
        producer_slug: string;
        errors: number;
        warns: number;
        latest_event_key: string;
        latest_level: string;
        latest_message: string;
        latest_at: string;
      }>();

      for (const row of data || []) {
        const pid = row.producer_id!;
        const producer = row.producer as any;
        if (!producer?.name) continue;

        if (!map.has(pid)) {
          map.set(pid, {
            producer_id: pid,
            producer_name: producer.name,
            producer_slug: producer.slug || '',
            errors: 0,
            warns: 0,
            latest_event_key: row.event_key,
            latest_level: row.level,
            latest_message: row.message,
            latest_at: row.created_at,
          });
        }

        const entry = map.get(pid)!;
        if (row.level === 'error') entry.errors++;
        else entry.warns++;
      }

      // Convert to array and sort by latest event (already sorted from query)
      const rows: HostHealthRow[] = Array.from(map.values()).map(e => ({
        producer_id: e.producer_id,
        producer_name: e.producer_name,
        producer_slug: e.producer_slug,
        status: e.errors > 0 ? 'red' : 'yellow' as HealthStatus,
        error_count: e.errors,
        warn_count: e.warns,
        latest_event_key: e.latest_event_key,
        latest_level: e.latest_level,
        latest_message: e.latest_message,
        latest_at: e.latest_at,
      }));

      // Sort: red first, then by latest_at desc
      rows.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'red' ? -1 : 1;
        return new Date(b.latest_at).getTime() - new Date(a.latest_at).getTime();
      });

      return rows.slice(0, 50);
    },
  });
}
